import {
  createReference,
  evalFhirPath,
  extractAccountReferences,
  getReferenceString,
  parseReference,
  resolveId,
} from "@medplum/core";
import type { MedplumClient } from "@medplum/core";
import type { Organization, Patient, Practitioner, Reference, Resource } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import { getResourcesFromBundle, joinReferenceString } from "./medplum";
import { isMedplumForbiddenError, isMedplumNotFoundError } from "./medplum-forbidden";

// Patient compartment fields from FHIR R4 spec (actual FHIRPath field names)
const PATIENT_COMPARTMENT_FIELDS: Record<string, string[]> = {
  Account: ["subject"],
  AdverseEvent: ["subject"],
  AllergyIntolerance: ["patient", "recorder", "asserter"],
  Appointment: ["participant.actor"],
  AppointmentResponse: ["actor"],
  AuditEvent: ["agent.who", "entity.what"],
  Basic: ["subject", "author"],
  BodyStructure: ["patient"],
  CarePlan: ["subject", "activity.detail.performer"],
  CareTeam: ["subject", "participant.member"],
  ChargeItem: ["subject"],
  Claim: ["patient", "payee.party"],
  ClaimResponse: ["patient"],
  ClinicalImpression: ["subject"],
  Communication: ["subject", "sender", "recipient"],
  CommunicationRequest: ["subject", "sender", "recipient", "requester"],
  Composition: ["subject", "author", "attester.party"],
  Condition: ["subject", "asserter"],
  Consent: ["patient"],
  Coverage: ["policyHolder", "subscriber", "beneficiary", "payor"],
  CoverageEligibilityRequest: ["patient"],
  CoverageEligibilityResponse: ["patient"],
  DetectedIssue: ["patient"],
  DeviceRequest: ["subject", "performer"],
  DeviceUseStatement: ["subject"],
  DiagnosticReport: ["subject"],
  DocumentManifest: ["subject", "author", "recipient"],
  DocumentReference: ["subject", "author"],
  Encounter: ["subject"],
  EnrollmentRequest: ["candidate"],
  EpisodeOfCare: ["patient"],
  ExplanationOfBenefit: ["patient", "payee.party"],
  FamilyMemberHistory: ["patient"],
  Flag: ["subject"],
  Goal: ["subject"],
  Group: ["member.entity"],
  ImagingStudy: ["subject"],
  Immunization: ["patient"],
  ImmunizationEvaluation: ["patient"],
  ImmunizationRecommendation: ["patient"],
  Invoice: ["subject", "recipient"],
  List: ["subject", "source"],
  MeasureReport: ["subject"],
  Media: ["subject"],
  MedicationAdministration: ["subject", "performer.actor"],
  MedicationDispense: ["subject", "receiver"],
  MedicationRequest: ["subject"],
  MedicationStatement: ["subject"],
  MolecularSequence: ["patient"],
  NutritionOrder: ["patient"],
  Observation: ["subject", "performer"],
  Patient: ["link.other"],
  Person: ["link.target"],
  Procedure: ["subject", "performer.actor"],
  Provenance: ["target"],
  QuestionnaireResponse: ["subject", "author"],
  RelatedPerson: ["patient"],
  RequestGroup: ["subject"],
  ResearchSubject: ["individual"],
  RiskAssessment: ["subject"],
  Schedule: ["actor"],
  ServiceRequest: ["subject", "performer"],
  Specimen: ["subject"],
  SupplyDelivery: ["patient"],
  SupplyRequest: ["requester"],
  Task: ["for", "focus"],
  VisionPrescription: ["patient"],
};

// Organization fields — resource types mapped to actual FHIRPath field names that can reference Organization
const ORGANIZATION_FIELDS: Record<string, string[]> = {
  Account: ["owner"],
  ChargeItem: ["performingOrganization", "requestingOrganization"],
  Claim: ["insurer", "provider"],
  Communication: ["recipient", "sender"],
  CommunicationRequest: ["recipient", "sender", "requester"],
  Composition: ["author"],
  Contract: ["authority"],
  Coverage: ["payor"],
  Device: ["owner"],
  DiagnosticReport: ["performer"],
  DocumentReference: ["custodian"],
  Encounter: ["serviceProvider"],
  Endpoint: ["managingOrganization"],
  EpisodeOfCare: ["managingOrganization"],
  ExplanationOfBenefit: ["insurer", "provider"],
  HealthcareService: ["providedBy"],
  InsurancePlan: ["ownedBy", "administeredBy"],
  Location: ["managingOrganization"],
  MeasureReport: ["reporter"],
  MedicationRequest: ["requester", "performer"],
  Observation: ["performer"],
  Organization: ["partOf"],
  OrganizationAffiliation: ["organization", "participatingOrganization"],
  Patient: ["managingOrganization"],
  PractitionerRole: ["organization"],
  ResearchStudy: ["sponsor"],
  ServiceRequest: ["performer", "requester"],
  SupplyDelivery: ["supplier"],
  SupplyRequest: ["supplier"],
  Task: ["owner"],
};

/**
 * Extracts all Organization references from a resource.
 * Checks both meta.compartment (for compartment-based membership) and
 * specific resource fields using ORGANIZATION_FIELD_PARAMS.
 *
 * @param resource - The resource to get organization references for
 * @returns An array of organization references
 */
export function getOrganizationReferences(resource: Resource): Reference<Organization>[] {
  if (!resource) return [];

  const result = new Set<string>();

  if (resource.resourceType === "Organization" && resource.id) {
    result.add(getReferenceString(resource)!);
  }

  for (const ref of resource.meta?.compartment ?? []) {
    if (ref.reference?.startsWith("Organization/")) {
      result.add(ref.reference);
    }
  }

  const fields = ORGANIZATION_FIELDS[resource.resourceType];
  if (fields) {
    for (const field of fields) {
      try {
        const values = evalFhirPath(field, resource);
        for (const value of values) {
          if (value && typeof value === "object" && "reference" in value) {
            const reference = getReferenceString(value as Reference);
            if (reference?.startsWith("Organization/")) {
              result.add(reference);
            }
          }
        }
      } catch (error) {
        console.warn(error);
      }
    }
  }

  return Array.from(result).map((reference) => ({ reference }));
}

/**
 * Gets the organization references that should be applied to a resource.
 * For Practitioner resources, organizations are resolved via PractitionerRole.
 * For all other resources, organizations are resolved from getOrganizationReferences.
 *
 * @param medplum - The MedplumClient instance
 * @param resource - The resource to get organizations for
 * @returns Array of organization references linked to the resource
 */
export async function getOrganizationsFromAccounts(
  medplum: MedplumClient,
  resource: Resource,
): Promise<Reference<Organization>[]> {
  let organizationReferences: Reference<Organization>[] = [];

  if (resource.resourceType === "Practitioner") {
    const { bundle } = await medplum.searchResources("PractitionerRole", {
      practitioner: getReferenceString(resource as Practitioner),
      active: true,
      _count: 10000,
      _elements: "organization",
    });

    const practitionerRoles = getResourcesFromBundle("PractitionerRole", bundle);

    organizationReferences = practitionerRoles
      .map((role) => role.organization)
      .filter(Boolean) as Reference<Organization>[];
  } else {
    organizationReferences = getOrganizationReferences(resource);
  }

  if (organizationReferences.length === 0) {
    return [];
  }

  const organizationIds = organizationReferences
    .map((reference) => resolveId(reference))
    .filter(Boolean) as string[];

  const { bundle } = await medplum.searchResources("Organization", {
    _id: organizationIds.join(","),
    _elements: "id",
    _count: organizationIds.length,
  });

  const organizations = getResourcesFromBundle("Organization", bundle);

  return organizations.map((organization) => createReference(organization));
}

/**
 * Extracts all Patient references from a resource using the Patient Compartment definition.
 * This is the client-side equivalent of the server's getPatients() function.
 *
 * @param resource - The resource to get patient references for
 * @returns An array of patient references
 */
export function getPatientReferences(resource: Resource): Reference<Patient>[] {
  const result = new Set<string>();

  if (!resource) return [];

  // If the resource IS a Patient, return self-reference
  if (resource.resourceType === "Patient" && resource.id) {
    result.add(getReferenceString(resource)!);
  }
  // Get compartment fields for this resource type
  const fields = PATIENT_COMPARTMENT_FIELDS[resource.resourceType];
  if (fields) {
    for (const field of fields) {
      try {
        const values = evalFhirPath(field, resource);
        for (const value of values) {
          if (value && typeof value === "object" && "reference" in value) {
            const reference = getReferenceString(value as Reference);
            if (reference?.startsWith("Patient/")) {
              result.add(reference);
            }
          }
        }
      } catch (error) {
        console.warn(error);
      }
    }
  }
  return Array.from(result).map((reference) => ({ reference }));
}

/**
 * Gets the accounts that should be applied to a resource based on its Patient reference(s).
 * This mimics what Medplum server does automatically when saving resources.
 *
 * @param medplum - The MedplumClient instance
 * @param resource - The resource to get accounts for
 * @returns Array of account references to set on the resource
 */
export async function getPatientAccounts(
  medplum: MedplumClient,
  resource: Resource,
): Promise<Reference[]> {
  const patientRefs = getPatientReferences(resource);

  if (patientRefs.length === 0) {
    return [];
  }
  const accounts = new Set<string>();
  // Read each patient and extract their accounts
  for (const patientRef of patientRefs) {
    const [resourceType, patientId] = parseReference(patientRef);
    const patient = await medplum.searchOne(resourceType, { _id: patientId, _elements: "id" });
    if (!patient) continue;

    const patientAccounts = extractAccountReferences(patient.meta);
    if (patientAccounts?.length) {
      for (const account of patientAccounts) {
        if (account.reference) {
          accounts.add(account.reference);
        }
      }
    }
  }

  if (accounts.size === 0) {
    return [];
  }
  return Array.from(accounts).map((reference) => ({ reference }));
}

/**
 * Sets the accounts on a resource using the $set-accounts bot.
 *
 * @param medplum - The MedplumClient instance
 * @param resource - The resource to set accounts for
 * @param accounts - The accounts to set on the resource
 * @returns The response from the bot execution
 */
export async function setAccounts(
  medplum: MedplumClient,
  resource: Resource,
  accounts: Reference<Resource>[],
  { extractFromPatient = false }: { extractFromPatient?: boolean } = {},
) {
  if (!resource.id) {
    throw new Error("Resource is required to have an ID");
  }

  return await medplum.post(medplum.fhirUrl("$qh-set-accounts"), {
    resource,
    accounts,
    extractFromPatient,
  });
}

interface QhSetAccountsResponse {
  result?: boolean;
}

/**
 * Sets accounts on a resource via Medplum's native `$set-accounts` operation.
 * Used when `$qh-set-accounts` cannot run (for example admin bots without a Practitioner requester).
 *
 * @param medplum - The MedplumClient instance
 * @param resource - The resource to set accounts for
 * @param accounts - Account references to assign
 * @param options.propagate - Also propagate the accounts to resources this one references. Needed
 *   when the resource's own compartment membership isn't otherwise derivable (e.g. RelatedPerson,
 *   whose subject-of-care reference doesn't establish the practice compartment on its own).
 */
export async function setResourceAccountsDirect(
  medplum: MedplumClient,
  resource: Resource,
  accounts: Reference<Resource>[],
  options?: { propagate?: boolean },
): Promise<void> {
  if (!resource.id) {
    throw new Error("Resource is required to have an ID");
  }

  if (accounts.length === 0) {
    throw new Error("At least one account reference is required");
  }

  try {
    await medplum.post(medplum.fhirUrl(resource.resourceType, resource.id, "$set-accounts"), {
      resourceType: "Parameters",
      parameter: [
        ...accounts.map((account) => ({
          name: "accounts",
          valueReference: account,
        })),
        ...(options?.propagate ? [{ name: "propagate", valueBoolean: true }] : []),
      ],
    });
  } catch (error) {
    if (isMedplumForbiddenError(error)) {
      throw new Error(
        `$set-accounts forbidden for ${resource.resourceType}/${resource.id}; confirm the caller has operation access`,
        { cause: error },
      );
    }

    throw error;
  }
}

/**
 * Returns whether a resource includes the organization in meta.compartment.
 *
 * @param resource - FHIR resource to inspect
 * @param organizationId - Organization logical id
 */
export function resourceHasOrganizationCompartment(
  resource: Resource,
  organizationId: string,
): boolean {
  const organizationReference = joinReferenceString("Organization", organizationId);

  return (
    resource.meta?.compartment?.some(
      (reference) => reference.reference === organizationReference,
    ) ?? false
  );
}

/**
 * Applies organization account metadata to a resource payload before create/update.
 * Admin bots can persist meta.accounts on write; `$set-accounts` still sets compartment.
 *
 * @param resource - Resource payload
 * @param organizationId - Organization logical id
 */
export function withOrganizationMetaAccounts<T extends Resource>(
  resource: T,
  organizationId: string,
): T {
  const organizationReference = { reference: joinReferenceString("Organization", organizationId) };
  const existingAccounts = extractAccountReferences(resource.meta) ?? resource.meta?.accounts ?? [];
  const mergedAccounts = mergeAccountReferences(existingAccounts, organizationReference);

  return {
    ...resource,
    meta: {
      ...resource.meta,
      account: resource.meta?.account ?? organizationReference,
      accounts: mergedAccounts,
    },
  };
}

function mergeAccountReferences(
  existingAccounts: Reference<Resource>[],
  organizationReference: Reference<Resource>,
): Reference<Resource>[] {
  const organizationReferenceString = organizationReference.reference;
  if (!organizationReferenceString) {
    return existingAccounts;
  }

  const hasOrganization = existingAccounts.some(
    (account) => account.reference === organizationReferenceString,
  );

  if (hasOrganization) {
    return existingAccounts;
  }

  return [...existingAccounts, organizationReference];
}

/**
 * Sets an Organization account on a resource via native `$set-accounts`.
 * Use for admin bots and other callers without a Practitioner requester.
 *
 * @param medplum - The MedplumClient instance
 * @param resource - The resource to set accounts for
 * @param organizationId - Organization logical id
 * @param options.propagate - See {@link setResourceAccountsDirect}.
 */
export async function setOrganizationAccountsDirectOnResource(
  medplum: MedplumClient,
  resource: Resource,
  organizationId: string,
  options?: { propagate?: boolean },
): Promise<void> {
  // A caller requesting propagation still needs the $set-accounts call to run even when this
  // resource's own compartment is already set — skipping here would also skip propagating to the
  // resources it references, which a prior, non-propagating call could never have done.
  if (!options?.propagate && resourceHasOrganizationCompartment(resource, organizationId)) {
    return;
  }

  await setResourceAccountsDirect(
    medplum,
    resource,
    [{ reference: joinReferenceString("Organization", organizationId) }],
    options,
  );

  const refreshed = await medplum.readResource(resource.resourceType, resource.id!);
  if (!resourceHasOrganizationCompartment(refreshed, organizationId)) {
    throw new Error(
      `${resource.resourceType}/${resource.id} is missing Organization/${organizationId} in meta.compartment after $set-accounts`,
    );
  }
}

/**
 * Copies patient-derived accounts onto a resource via native `$set-accounts`.
 * Use for admin bots and other callers without a Practitioner requester.
 *
 * @param medplum - The MedplumClient instance
 * @param resource - The resource whose patient compartment supplies accounts
 */
export async function setResourceAccountsFromPatientDirect(
  medplum: MedplumClient,
  resource: Resource,
): Promise<void> {
  const accounts = await getPatientAccounts(medplum, resource);
  if (accounts.length === 0) {
    return;
  }

  await setResourceAccountsDirect(medplum, resource, accounts);
}

/**
 * Ensures an Organization account is set on a resource for org-compartment visibility.
 * Uses `$qh-set-accounts` so Practitioner access and allowed-org checks run server-side.
 *
 * `$set-accounts` replaces the resource's accounts. A resource on a client's chart passes
 * `extractFromPatient`, so it keeps the Patient's accounts — the practitioners the chart is shared
 * with — alongside the Organization. Without it, only an org-wide role can reach the resource.
 *
 * @param medplum - The MedplumClient instance
 * @param resource - The resource to set accounts for
 * @param organizationId - Organization logical id
 * @param options.extractFromPatient - Also keep the accounts of the Patient the resource belongs to
 */
export async function setOrganizationAccountsOnResource(
  medplum: MedplumClient,
  resource: Resource,
  organizationId: string,
  { extractFromPatient = false }: { extractFromPatient?: boolean } = {},
): Promise<void> {
  const organizationReference = {
    reference: joinReferenceString("Organization", organizationId),
  };

  try {
    const response = (await setAccounts(medplum, resource, [organizationReference], {
      extractFromPatient,
    })) as QhSetAccountsResponse | undefined;

    if (response?.result === false) {
      throw new Error("$qh-set-accounts declined to set organization accounts");
    }
  } catch (error) {
    if (!isMedplumNotFoundError(error)) {
      throw error;
    }

    if (extractFromPatient) {
      const patientAccounts = await getPatientAccounts(medplum, resource);
      await setResourceAccountsDirect(
        medplum,
        resource,
        mergeOrganizationAccountFirst(patientAccounts, organizationReference),
      );
      return;
    }

    await setOrganizationAccountsDirectOnResource(medplum, resource, organizationId);
  }
}

/**
 * Invokes {@link setAccounts} but does not throw when the server returns Forbidden (403) or when
 * the `$qh-set-accounts` operation is unavailable (404), for example on local Medplum without
 * the bot deployed.
 *
 * Non-admin practitioners are often blocked from writing `meta.accounts` directly; the client
 * already retries QuestionnaireResponse saves without accounts. The `$qh-set-accounts` bot must
 * still be able to complete linkage — grant its ProjectMembership access consistent with an
 * admin-capable client if operators see Sentry warning-level reports and resources stay unlinked.
 *
 * Control flow: on HTTP 403 Forbidden or 404 Not Found, the error is reported to Sentry at warning
 * level and the function resolves `false` (no throw). HTTP 200 with `{ result: false }` is the same:
 * the operation declined the linkage without throwing, so this reports a warning and resolves
 * `false`. Any other rejection is rethrown.
 *
 * @returns Whether the linkage was actually set — `false` on a decline, 403 or 404.
 */
export async function setAccountsBestEffort(
  medplum: MedplumClient,
  resource: Resource,
  accounts: Reference<Resource>[],
  options?: { extractFromPatient?: boolean },
): Promise<boolean> {
  try {
    const response = (await setAccounts(medplum, resource, accounts, options)) as
      | QhSetAccountsResponse
      | undefined;

    if (response?.result === false) {
      Sentry.captureException(new Error("$qh-set-accounts declined to set organization accounts"), {
        level: "warning",
        tags: { feature: "accounts", operation: "qh-set-accounts" },
        extra: {
          resourceType: resource.resourceType,
          id: resource.id,
          message: "$qh-set-accounts returned result false; org linkage was skipped.",
        },
      });
      return false;
    }

    return true;
  } catch (error) {
    if (!isMedplumForbiddenError(error) && !isMedplumNotFoundError(error)) {
      throw error;
    }
    Sentry.captureException(error, {
      level: "warning",
      tags: { feature: "accounts", operation: "qh-set-accounts" },
      extra: {
        resourceType: resource.resourceType,
        id: resource.id,
        message: isMedplumNotFoundError(error)
          ? "$qh-set-accounts is not deployed; org linkage was skipped for this resource."
          : "$qh-set-accounts forbidden; org linkage may be missing until bot access is fixed.",
      },
    });
    return false;
  }
}

/**
 * Returns whether a resource is already linked to the organization, via either `meta.accounts`
 * or `meta.compartment`.
 *
 * Write paths use this to skip a redundant `$set-accounts` when create/update already applied
 * the linkage. A second `$set-accounts` can normalize meta and drop client fields (e.g.
 * `meta.accounts`) before downstream bots run.
 *
 * @param resource - FHIR resource to inspect
 * @param organizationAccountRef - Organization reference to look for
 */
export function resourceHasOrganizationLinkage(
  resource: Resource,
  organizationAccountRef: Reference<Organization>,
): boolean {
  const target = organizationAccountRef.reference;
  if (!target) return false;

  return Boolean(
    resource.meta?.accounts?.some((account) => account.reference === target) ||
      resource.meta?.compartment?.some((compartment) => compartment.reference === target),
  );
}

/**
 * Puts the organization account first, then keeps any other accounts already present.
 * Replacing the array instead of merging would strip linkage another practice relies on.
 *
 * Unlike `mergeAccountReferences`, the organization is ordered first because callers rely on it
 * being the primary account.
 *
 * @param previousAccounts - Accounts already on the resource payload
 * @param organizationAccountRef - Organization reference to place first
 */
export function mergeOrganizationAccountFirst(
  previousAccounts: Reference[] | undefined,
  organizationAccountRef: Reference<Organization>,
): Reference[] {
  const target = organizationAccountRef.reference;
  if (!target) return [...(previousAccounts ?? [])];

  const merged: Reference[] = [organizationAccountRef];
  const seen = new Set<string>([target]);

  for (const account of previousAccounts ?? []) {
    if (account.reference && !seen.has(account.reference)) {
      merged.push(account);
      seen.add(account.reference);
    }
  }

  return merged;
}
